import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  usePayRun, useDiscardPayRun, useApprovePayRun, useReopenPayRun,
  useMarkPaid, useVoidPayRun, useUpdatePayItem,
} from '../../hooks/usePayRuns.js';
import { useError } from '../../components/shared/ErrorBanner.jsx';
import { centsToUsd, iso } from '../../lib/format.js';
import PayRunMetaEditModal from '../../components/pay/PayRunMetaEditModal.jsx';

export default function PayRunDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { data } = usePayRun(id);
  const discard = useDiscardPayRun();
  const approve = useApprovePayRun();
  const reopen = useReopenPayRun();
  const markPaid = useMarkPaid();
  const voidRun = useVoidPayRun();
  const { show } = useError();
  const [editingMeta, setEditingMeta] = useState(false);

  if (!data) return <div className="loading">Loading…</div>;
  const { run, items } = data;
  const isDraft = run.status === 'draft';
  const grossTotal = items.reduce((s, it) => s + (it.gross_cents || 0), 0);

  const wrapAction = (mut, body, confirmMsg) => async () => {
    if (confirmMsg && !confirm(confirmMsg)) return;
    try { await mut.mutateAsync({ id, ...body }); }
    catch (err) { show(err.message); }
  };

  const onDiscard = async () => {
    if (!confirm('Discard this draft run?')) return;
    try {
      await discard.mutateAsync(id);
      nav('/admin/pay');
    } catch (err) { show(err.message); }
  };

  const busy = approve.isPending || reopen.isPending || markPaid.isPending || voidRun.isPending || discard.isPending;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Link className="btn-sm" to="/admin/pay">← Back</Link>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className={'badge' + (run.status === 'paid' ? ' orange' : '')}>{run.status}</span>
          <span className="badge">{run.provider}</span>
          {isDraft && (
            <button className="btn-sm" onClick={() => setEditingMeta(true)}>
              <i className="ph ph-pencil-simple" /> Edit
            </button>
          )}
        </div>
      </div>

      <div className="sum-row">
        <div className="sum-card">
          <div className="sum-card-label">Period</div>
          <div className="sum-card-value" style={{ fontSize: 16 }}>
            {iso(run.period_start)} → {iso(run.period_end - 1)}
          </div>
        </div>
        <div className="sum-card">
          <div className="sum-card-label">Pay date</div>
          <div className="sum-card-value" style={{ fontSize: 16 }}>
            {run.pay_date ? iso(run.pay_date) : '—'}
          </div>
        </div>
        <div className="sum-card">
          <div className="sum-card-label">Items</div>
          <div className="sum-card-value">{items.length}</div>
        </div>
        <div className="sum-card">
          <div className="sum-card-label">Gross total</div>
          <div className="sum-card-value orange">{centsToUsd(grossTotal)}</div>
        </div>
      </div>

      <table className="tbl">
        <thead>
          <tr>
            <th>Crew</th>
            <th className="num">Reg / OT</th>
            <th className="num col-meta">Reg pay</th>
            <th className="num col-meta">OT pay</th>
            <th className="num col-meta">Bonus</th>
            <th className="num col-meta">Deduct</th>
            <th className="num col-meta">Reimb</th>
            <th className="num">Gross</th>
            <th className="col-meta">Bank</th>
            <th>Status</th>
            {isDraft && <th></th>}
          </tr>
        </thead>
        <tbody>
          {items.map(it => (
            <PayItemRow key={it.id} it={it} isDraft={isDraft} runId={id} />
          ))}
        </tbody>
      </table>

      <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
        {isDraft && (
          <>
            <button className="btn-sm primary" disabled={busy}
              onClick={wrapAction(approve, {}, `Approve ${items.length} items? Hours will be locked.`)}>
              Approve run
            </button>
            <button className="btn-sm" disabled={busy} onClick={onDiscard}>Discard draft</button>
          </>
        )}
        {run.status === 'approved' && (
          <>
            <ExportButtons runId={id} provider={run.provider} />
            <button className="btn-sm primary" disabled={busy}
              onClick={() => {
                const d = prompt('Mark all items as paid. Pay date (YYYY-MM-DD) — leave blank for today:');
                if (d === null) return;
                const paidAt = d ? Date.parse(d + 'T00:00:00Z') : Date.now();
                wrapAction(markPaid, { paid_at: paidAt })();
              }}>
              Mark paid
            </button>
            <button className="btn-sm" disabled={busy}
              onClick={() => {
                const reason = prompt('Reopen to draft — reason?');
                if (!reason) return;
                wrapAction(reopen, { reason })();
              }}>
              Reopen
            </button>
          </>
        )}
        {run.status === 'paid' && (
          <>
            <ExportButtons runId={id} provider={run.provider} />
            <button className="btn-sm" disabled={busy}
              onClick={() => {
                const reason = prompt('Void this paid run — reason?');
                if (!reason) return;
                wrapAction(voidRun, { reason })();
              }}>
              Void run
            </button>
          </>
        )}
      </div>

      {editingMeta && (
        <PayRunMetaEditModal run={run} onClose={() => setEditingMeta(false)} />
      )}
    </div>
  );
}

function ExportButtons({ runId, provider }) {
  return (
    <>
      <a className="btn-sm" href={`/api/pay/runs/${runId}/export?format=csv`}>↓ CSV</a>
      {provider === 'nacha' && <a className="btn-sm" href={`/api/pay/runs/${runId}/export?format=nacha`}>↓ NACHA</a>}
      {provider === 'check' && <a className="btn-sm" href={`/api/pay/runs/${runId}/export?format=check`}>↓ Check preview</a>}
    </>
  );
}

function PayItemRow({ it, isDraft, runId }) {
  const [editing, setEditing] = useState(false);

  if (editing && isDraft) {
    return (
      <tr>
        <td colSpan="11" style={{ padding: 0 }}>
          <PayItemEditForm it={it} onDone={() => setEditing(false)} />
        </td>
      </tr>
    );
  }

  const banked = it.bank_account_last4 ? `••${it.bank_account_last4}` : '—';
  return (
    <tr>
      <td>{it.user_name}</td>
      <td className="num">{it.regular_hrs.toFixed(2)} / {it.ot_hrs.toFixed(2)}</td>
      <td className="num col-meta">{centsToUsd(it.regular_pay_cents)}</td>
      <td className="num col-meta">{centsToUsd(it.ot_pay_cents)}</td>
      <td className="num col-meta">{centsToUsd(it.bonus_cents)}</td>
      <td className="num col-meta">{centsToUsd(it.deduction_cents)}</td>
      <td className="num col-meta">{centsToUsd(it.reimbursement_cents)}</td>
      <td className="num"><strong>{centsToUsd(it.gross_cents)}</strong></td>
      <td className="col-meta" style={{ fontFamily: 'var(--font-label)', fontSize: 11, color: 'var(--warm-gray)' }}>{banked}</td>
      <td><span className={'badge' + (it.status === 'paid' ? ' orange' : '')}>{it.status}</span></td>
      {isDraft && (
        <td style={{ textAlign: 'right' }}>
          <button className="btn-sm" onClick={() => setEditing(true)}>Edit</button>{' '}
          <a className="btn-sm" href={`/api/pay/runs/${runId}/stubs/${it.id}`} target="_blank" rel="noopener">Stub</a>
        </td>
      )}
    </tr>
  );
}

function PayItemEditForm({ it, onDone }) {
  const [bonus, setBonus]   = useState((it.bonus_cents / 100).toFixed(2));
  const [deduct, setDeduct] = useState((it.deduction_cents / 100).toFixed(2));
  const [reimb, setReimb]   = useState((it.reimbursement_cents / 100).toFixed(2));
  const [reason, setReason] = useState(it.adjustment_reason || '');
  const [status, setStatus] = useState(it.status);
  const update = useUpdatePayItem();
  const { show } = useError();

  const save = async (e) => {
    e.preventDefault();
    try {
      await update.mutateAsync({
        id: it.id,
        bonus_cents: Math.round(Number(bonus) * 100),
        deduction_cents: Math.round(Number(deduct) * 100),
        reimbursement_cents: Math.round(Number(reimb) * 100),
        adjustment_reason: reason || null,
        status,
      });
      onDone();
    } catch (err) { show(err.message); }
  };

  return (
    <form onSubmit={save} style={{ padding: 16, background: 'var(--surface-container)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        <div className="field"><label className="field-label">Bonus ($)</label>
          <input className="field-input" type="number" step="0.01" value={bonus} onChange={e => setBonus(e.target.value)} /></div>
        <div className="field"><label className="field-label">Deduction ($)</label>
          <input className="field-input" type="number" step="0.01" value={deduct} onChange={e => setDeduct(e.target.value)} /></div>
        <div className="field"><label className="field-label">Reimbursement ($)</label>
          <input className="field-input" type="number" step="0.01" value={reimb} onChange={e => setReimb(e.target.value)} /></div>
        <div className="field"><label className="field-label">Status</label>
          <select className="field-select" value={status} onChange={e => setStatus(e.target.value)}>
            <option value="pending">pending</option>
            <option value="skipped">skipped (do not pay)</option>
            <option value="void">void</option>
          </select></div>
      </div>
      <div className="field">
        <label className="field-label">Adjustment reason</label>
        <input className="field-input" value={reason} onChange={e => setReason(e.target.value)}
          placeholder="e.g. signing bonus, garnishment, expense reimbursement…" />
      </div>
      <button className="btn-sm primary" type="submit" disabled={update.isPending}>
        {update.isPending ? 'Saving…' : 'Save'}
      </button>
    </form>
  );
}
